import React from 'react'
import Header from '../../Global/Header'
import Footer from '../../Global/Footer'
import Bredcump from '../../Global/Bredcump'
import { brandUrl } from '../../../constants/brand'

const Main = () => {
  return (
    <div>
      <Header />
      <Bredcump
        Heading="Legal"
        Title="Privacy Policy"
        Subtitle="Privacy"
        Description="How we collect, use, and protect your information when you use the Noor app and noorhc.app."
      />
      <section className="row_am section_inner_padding legal_doc">
        <div className="container">
          <p className="legal_updated"><strong>Last Updated: August 23, 2026</strong></p>

          <h2>Introduction</h2>
          <p>
            Noor (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) is committed to protecting your privacy. This Privacy
            Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile
            application (&quot;App&quot;) and our marketing website at {brandUrl('marketing')} (the
            &quot;Site&quot;). Please read this policy carefully. By using the App or submitting information through
            the Site, you agree to the collection and use of information as described here.
          </p>

          <h2>Information We Collect</h2>
          <h3>Personal Information</h3>
          <ul>
            <li>
              <strong>Account Information</strong>: Email address, name, and profile information you provide
              during registration
            </li>
            <li>
              <strong>Authentication Data</strong>: Account credentials managed securely through our
              authentication provider
            </li>
          </ul>
          <h3>Health-related Data</h3>
          <ul>
            <li>
              <strong>Emotional Check-ins</strong>: Mood ratings, emotional states, and check-in timestamps
            </li>
            <li>
              <strong>Journal Entries</strong>: Journal content, mood tags, and entry metadata
            </li>
            <li>
              <strong>Habit Tracking</strong>: Habit completion data, habit names, and tracking history
            </li>
            <li>
              <strong>Fitness Data</strong>: Workout routines, exercise logs, and fitness progress
            </li>
            <li>
              <strong>Progress Data</strong>: Module completion, progress tracking, and engagement metrics
            </li>
          </ul>
          <h3>Usage Data</h3>
          <ul>
            <li>
              <strong>App Activity</strong>: Features used, modules accessed, and interaction patterns
            </li>
            <li>
              <strong>Device Information</strong>: Device type, operating system version, and app version
            </li>
            <li>
              <strong>Crash and Performance Signals</strong>: Used to keep the App reliable
            </li>
          </ul>
          <h3>Service Integrity and Safety Monitoring</h3>
          <ul>
            <li>
              Lesson and feature usage events (for example completions, progress, drop-off) used to keep
              content working and detect misuse
            </li>
            <li>
              Integrity and abuse-prevention signals related to AI and other high-risk features (for example
              rate, pattern, and anomaly indicators)
            </li>
            <li>Safety-related product events that help us protect users and the App</li>
            <li>
              These events are <strong>minimised</strong>. They do <strong>not</strong> include journal body
              text or crisis matched phrases
            </li>
          </ul>
          <h3>Subscription Information</h3>
          <ul>
            <li>
              <strong>Purchase Data</strong>: Subscription tier, purchase history, and payment status
              (processed through Apple/Google)
            </li>
            <li>
              <strong>RevenueCat Data</strong>: Subscription status and entitlements (managed by RevenueCat)
            </li>
          </ul>

          <h3>Marketing Website Forms</h3>
          <p>
            If you use forms on {brandUrl('marketing')} without an App account, we may collect:
          </p>
          <ul>
            <li>
              <strong>Newsletter sign-up</strong>: Email address when you subscribe in the site footer for
              occasional product updates and wellness tips
            </li>
            <li>
              <strong>Contact form</strong>: Name, email address, message content, and technical metadata such as
              the page you submitted from and browser user agent string
            </li>
            <li>
              <strong>Spam-control signals</strong>: A hidden honeypot field that legitimate browsers leave empty;
              submissions that fill it are rejected and not stored
            </li>
          </ul>
          <p>
            We do <strong>not</strong> use these website forms to collect App health data, journal content, or
            subscription payment details. Payment for subscriptions happens only in the App Store or Google Play
            through the App.
          </p>

          <h2>How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li><strong>Provide Services</strong>: Deliver modules, tools, and features</li>
            <li><strong>Personalize Experience</strong>: Customize content recommendations and user interface</li>
            <li><strong>Process Subscriptions</strong>: Manage subscription purchases and access control</li>
            <li>
              <strong>Service Integrity and Safety</strong>: Detect misuse and abuse (including of AI features),
              protect user wellbeing within the product, and maintain a safe operating environment
            </li>
            <li>
              <strong>Improve App</strong>: Analyse lesson completion, engagement trends, and product quality so
              we can keep content and features effective
            </li>
            <li>
              <strong>Communicate</strong>: Send important updates, support responses, and service notifications
            </li>
            <li>
              <strong>Website enquiries and updates</strong>: Respond to contact form messages, operate newsletter
              sign-ups, and send occasional product updates where you have subscribed on the Site
            </li>
            <li>
              <strong>Comply with Legal Obligations</strong>: Meet legal requirements and protect our rights
            </li>
          </ul>

          <h3>Required Monitoring (No In-App Opt-Out)</h3>
          <ul>
            <li>
              Service integrity and safety monitoring is <strong>required</strong> to use Noor. There is no
              in-app setting to turn it off
            </li>
            <li>
              This monitoring supports abuse prevention, AI misuse detection, user safety flows, lesson and
              product quality, crash diagnosis, and related integrity controls
            </li>
            <li>
              We minimise what we collect. We do <strong>not</strong> use this monitoring to sell your personal
              information or for third-party advertising
            </li>
            <li>
              If you do not want this processing, do not use the App, or delete your account (Profile → Account
              Settings → Delete Account)
            </li>
          </ul>

          <h3>AI-Assisted Features</h3>
          <ul>
            <li>
              When you use AI-assisted features, we may process your prompts and relevant in-app data to
              generate responses for you
            </li>
            <li>AI processing is used to provide the feature, keep it secure, and prevent abuse</li>
            <li>
              AI-assisted features are <strong>not</strong> a substitute for professional care. Do not submit
              information you are not comfortable processing for this purpose
            </li>
            <li>
              We minimise what is sent for AI features and do not include journal body text in routine product
              analytics events described above
            </li>
          </ul>

          <h3>Journal Wellbeing Checks (Automated, On-Device)</h3>
          <ul>
            <li>
              When you save certain journal entries, the App runs automated text analysis <strong>on your
              device</strong> using in-house rules (not generative AI) to determine whether to show{' '}
              <strong>local</strong> crisis support resources for your device region (for example Samaritans in
              the UK, 988 in the US)
            </li>
            <li>
              <strong>Your journal text is not transmitted to our servers for this check</strong> in the current
              version of the App. The entry is saved as usual; the check does not block saving
            </li>
            <li>
              We may record <strong>analytics events</strong> when a wellbeing check triggers or when you view
              crisis resources (for example risk level and journal flow type). These events{' '}
              <strong>do not include your journal text</strong>, matched phrases, or internal rule identifiers
            </li>
            <li>
              Cooldown timestamps may be stored locally on your device to avoid repeated prompts. See Help →
              Crisis resources in the App
            </li>
          </ul>

          <h2>Data Storage and Security</h2>
          <h3>Data Storage</h3>
          <ul>
            <li>Your data is stored securely using Supabase (PostgreSQL database)</li>
            <li>Data is encrypted in transit and at rest</li>
            <li>We implement industry-standard security measures to protect your information</li>
          </ul>
          <h3>Security Measures</h3>
          <ul>
            <li>Secure authentication and authorization</li>
            <li>Regular security reviews and updates</li>
            <li>Access controls limiting data access to authorized personnel</li>
            <li>Data backup and recovery procedures</li>
          </ul>

          <h2>Third-Party Services</h2>
          <h3>Supabase</h3>
          <p>
            <strong>Purpose</strong>: Backend infrastructure, database, and authentication.{' '}
            <strong>Data</strong>: User accounts, app data, authentication tokens, and marketing website form
            submissions (newsletter sign-ups and contact messages).{' '}
            <strong>Privacy</strong>: Supabase&apos;s privacy policy applies to their services.{' '}
            <strong>Location</strong>: Data may be stored in cloud servers.
          </p>
          <h3>RevenueCat</h3>
          <p>
            <strong>Purpose</strong>: Subscription management and in-app purchase processing.{' '}
            <strong>Data</strong>: Subscription status, purchase history, and user identifiers.{' '}
            <strong>Privacy</strong>: RevenueCat&apos;s privacy policy applies to their services.
          </p>
          <h3>PostHog</h3>
          <p>
            <strong>Purpose</strong>: Service integrity and safety monitoring, product analytics for lesson and
            feature quality, crash and performance diagnosis, and abuse detection.{' '}
            <strong>Data</strong>: Usage and integrity events, device and app metadata, and related product
            signals (not journal body text). <strong>Privacy</strong>: PostHog&apos;s privacy policy applies to
            their services.
          </p>
          <h3>Apple App Store / Google Play</h3>
          <p>
            <strong>Purpose</strong>: App distribution and payment processing.{' '}
            <strong>Data</strong>: Purchase transactions and payment information.{' '}
            <strong>Privacy</strong>: Apple/Google privacy policies apply to their services.
          </p>

          <h2>Data Sharing and Disclosure</h2>
          <p>We do <strong>not</strong> sell your personal information. We may share information only:</p>
          <ul>
            <li>
              <strong>Service Providers</strong>: With trusted providers who help operate the App (Supabase,
              RevenueCat, PostHog, and AI infrastructure providers when you use AI features)
            </li>
            <li><strong>Legal Requirements</strong>: When required by law, court order, or regulation</li>
            <li>
              <strong>Protection of Rights</strong>: To protect our rights, property, safety, or that of our
              users
            </li>
            <li>
              <strong>Business Transfers</strong>: In connection with a merger, acquisition, or sale of assets
              (with notice where required)
            </li>
          </ul>

          <h2>Your Rights and Choices</h2>
          <h3>Access and Correction</h3>
          <ul>
            <li>Access and update personal information in App settings</li>
            <li>Request a copy of your data by contacting us</li>
            <li>
              Ask us to correct or delete information you submitted through the Site contact form or newsletter
              sign-up
            </li>
          </ul>
          <h3>Data Deletion</h3>
          <ul>
            <li>
              Delete your account in Profile → Account Settings → Delete Account. Deletion is scheduled for 24
              hours; log in before then to cancel
            </li>
            <li>After 24 hours your account and associated data are permanently removed</li>
            <li>Some data may be retained as required by law or for legitimate business purposes</li>
          </ul>
          <h3>Subscription Management</h3>
          <p>Manage subscriptions through your device&apos;s App Store or Google Play settings.</p>
          <h3>Choices That Remain</h3>
          <ul>
            <li>Location services can be disabled in device settings where the App uses them</li>
            <li>
              Service integrity and safety monitoring <strong>cannot</strong> be disabled while using the App
              (see Required Monitoring). Your exit option is to stop using the App or delete your account
            </li>
          </ul>

          <h2>Children&apos;s Privacy</h2>
          <p>
            The App is intended for users <strong>aged 16 and older</strong>. We do not knowingly collect
            personal information from anyone under 16. If you believe we have collected information from
            someone under 16, contact us immediately and we will take appropriate steps to delete it.
          </p>

          <h2>International Users</h2>
          <h3>GDPR (European Users)</h3>
          <p>If you are in the European Economic Area (EEA), you may have rights including:</p>
          <ul>
            <li>Access, rectification, erasure, restriction, portability, and objection</li>
            <li>Withdraw consent where processing is based on consent</li>
            <li>Lodge a complaint with your local supervisory authority</li>
          </ul>
          <p>
            Some processing is <strong>required</strong> to operate the App safely (see Required Monitoring).
            Objecting to that processing means you must stop using the App or delete your account.
          </p>
          <h3>CCPA (California Users)</h3>
          <p>If you are a California resident, you have the right to:</p>
          <ul>
            <li>Know what personal information is collected</li>
            <li>Know whether personal information is sold or disclosed (we do not sell personal information)</li>
            <li>Access and delete personal information</li>
            <li>Non-discrimination for exercising your privacy rights</li>
          </ul>

          <h2>Data Retention</h2>
          <p>
            We retain information while your account is active or as needed to provide services. We may retain
            certain information:
          </p>
          <ul>
            <li>As required by law</li>
            <li>To resolve disputes and enforce agreements</li>
            <li>For legitimate business purposes</li>
          </ul>
          <p>
            When you delete your account, we delete or anonymize personal information except where retention is
            required by law.
          </p>

          <h2>Changes to This Privacy Policy</h2>
          <p>We may update this Privacy Policy. We will notify you of material changes by:</p>
          <ul>
            <li>Posting the new Privacy Policy in the App</li>
            <li>Updating the &quot;Last Updated&quot; date</li>
            <li>Sending a notification for material changes where appropriate</li>
          </ul>
          <p>
            Continued use after changes become effective constitutes acceptance of the updated Privacy Policy.
          </p>

          <h2>Contact Us</h2>
          <p>Questions, concerns, or requests regarding this Privacy Policy:</p>
          <p>
            <strong>Email</strong>: support@noorhc.app<br />
            <strong>Website</strong>: {brandUrl('marketing')}
          </p>

          <h2>Consent</h2>
          <p>
            By using the App or submitting information through the Site, you consent to the collection and use of
            information as described in this Privacy Policy. If you do not agree, do not use the App or submit
            forms on the Site.
          </p>

          <p className="legal_footer">
            <em>
              This Privacy Policy is effective as of August 23, 2026 and applies to all users of the Noor
              mobile application.
            </em>
          </p>
        </div>
      </section>
      <Footer />
    </div>
  )
}

export default Main
